import HttpClient from "@lib/http-client";

const integrationPath = "/integrations";
const connectionPath = `${integrationPath}/connection`;
const providerPath = `${integrationPath}/providers`;
const executionPath = `${integrationPath}/execution`;
const preconfigPath = `${integrationPath}/preconfig`;

export const IdpTypes = {
  NYCDOE: 1,
  CLEVER: 2,
  CLASSLINK: 3,
};

export const StaticProviderDetails = {
  [IdpTypes.CLEVER]: {
    connectionId: process.env.CLEVER_CONNECTION_ID,
    providerName: "clever",
  },
  [IdpTypes.CLASSLINK]: {
    connectionId: process.env.CLASSLINK_CONNECTION_ID,
    providerName: "classlink",
  },
};

export default class IntegrationService extends HttpClient {
  getProviders(params = { includeDisabled: false }) {
    const apiUrl = providerPath;
    return this.http.get(apiUrl, { params });
  }

  getProviderData(providerId) {
    const apiUrl = `${providerPath}/${providerId}`;
    return this.http.get(apiUrl);
  }

  createIntegrationConnection(providerId, clientCode, userId, body) {
    const apiUrl = `${connectionPath}/create`;
    const params = {
      provider_id: providerId,
      organization_code: clientCode,
      user_id: userId,
    };
    return this.http.post(apiUrl, body, { params });
  }

  getAuthUrl(providerId, connectionId) {
    // TODO: Path should be updated to match integrations platform
    const apiUrl = `${integrationPath}/authorization_url`;
    const params = {
      provider_id: providerId,
      connection_id: connectionId,
    };
    return this.http.get(apiUrl, { params });
  }

  syncIntegration(connectionId, userId) {
    const apiUrl = `${connectionPath}/${connectionId}/sync`;
    const params = {
      user_id: userId,
    };
    return this.http.post(apiUrl, null, { params });
  }

  getConnectionStatus(connectionId) {
    const apiUrl = `${connectionPath}/${connectionId}/status`;
    return this.http.get(apiUrl);
  }

  getConnectionsList(clientCode) {
    const apiUrl = connectionPath;
    const params = {
      client_code: clientCode,
    };
    return this.http.get(apiUrl, { params });
  }

  getProviderIconSquare(provider_id) {
    // TODO: Path should be updated to match integrations platform
    const apiUrl = `${integrationPath}/icon-square`;
    const params = {
      provider_id,
    };
    return this.http.get(apiUrl, { params });
  }

  getProviderIconRect(provider_id) {
    // TODO: Path should be updated to match integrations platform
    const apiUrl = `${integrationPath}/icon-rect`;
    const params = {
      provider_id,
    };
    return this.http.get(apiUrl, { params });
  }

  removeIntegration(connectionId) {
    const apiUrl = `${connectionPath}/${connectionId}`;
    return this.http.delete(apiUrl);
  }

  getSyncHistory(connectionId, offset = 0, take = 3, orgCode) {
    const apiUrl = `${connectionPath}/${connectionId}/history`;
    const params = {
      take,
      offset,
    };

    if (orgCode) {
      params.clientCode = orgCode;
    }

    return this.http.get(apiUrl, { params });
  }

  getEventDetails(jobExecutionId, resourceType, eventType) {
    const apiUrl = `${executionPath}/${jobExecutionId}/events`;
    const params = {
      resource_type: resourceType,
      event_type: eventType,
    };
    return this.http.get(apiUrl, { params });
  }

  getSyncDetails(jobExecutionId) {
    const requestConfig = {
      method: "get",
      url: `${executionPath}/${jobExecutionId}/csv`,
    };
    return this.downloadBlob(
      requestConfig,
      `sync-details-${jobExecutionId}`,
      "csv"
    );
  }

  getPreconfigEnabledForProvider(providerName) {
    const apiUrl = `${preconfigPath}/provider/${providerName}/enabled`;
    return this.http.get(apiUrl);
  }

  triggerPreconfigSync(connectionId, clientCode) {
    const apiUrl = `${preconfigPath}/${connectionId}/sync`;
    const params = {
      client_code: clientCode,
    };
    return this.http.post(apiUrl, null, { params });
  }

  getPreconfigTree(connectionId, clientCode) {
    const apiUrl = `${preconfigPath}/${connectionId}`;
    const params = {
      client_code: clientCode,
    };
    return this.http.get(apiUrl, { params });
  }

  savePreconfigTree(connectionId, clientCode, tree) {
    const apiUrl = `${preconfigPath}/${connectionId}`;
    const params = {
      client_code: clientCode,
    };
    return this.http.post(apiUrl, tree, { params });
  }

  getConnectionProviderLastRun(providerName, orgCodes) {
    if (!providerName) {
      return Promise.resolve(undefined);
    }

    if (!Array.isArray(orgCodes) || orgCodes.length === 0) {
      return Promise.resolve({});
    }

    const apiUrl = `${connectionPath}/${providerName}/last`;
    const params = {
      clientCode: orgCodes,
    };

    return this.http.get(apiUrl, {
      params,
      paramsSerializer: { indexes: null },
    });
  }

  getSyncErrors(connectionId, jobId, orgCode) {
    const apiUrl = `${connectionPath}/${connectionId}/sync/errors/${jobId}`;
    const params = orgCode ? { clientCode: orgCode } : {};
    return this.http.get(apiUrl, { params });
  }
}
